export const costingWorkspaceResources = {
  en: {
    page: {
      title: 'Cost & profitability',
      description: 'Traceable cost, deterministic allocation, period variance, profitability, pricing, and management analysis.',
      summary: 'Costing summary',
      tenantScoped: 'Tenant-scoped authoritative data',
    },
    metrics: {
      objects: 'Cost objects', direct: 'Direct attributed cost', unattributed: 'Unattributed cost',
      pendingRuns: 'Allocation runs awaiting action', finalizedAnalyses: 'Finalized analyses',
      historicalVersions: 'Historical cost versions', pricingChecks: 'Pricing checks',
      pendingApprovals: 'Approvals awaiting action',
    },
    workbench: {
      title: 'Costing workbench',
      description: 'Backend calculations are authoritative; every result retains its source identity.',
      objects: 'Objects & dimensions', entries: 'Cost explorer', allocation: 'Allocation',
      analytics: 'Profitability & decisions',
    },
    actions: {
      newObject: 'New cost object', newCenter: 'New cost center', newCategory: 'New category',
      newDriver: 'New driver', newRule: 'New rule', approve: 'Approve', finalize: 'Finalize',
      reverse: 'Reverse', calculate: 'Calculate period', newVersion: 'New cost version',
      checkPrice: 'Check price', newScenario: 'New scenario', costBenefit: 'Cost-benefit',
      askVarza: 'Ask Varza about costs', immutable: 'Immutable',
    },
    dialogs: {
      object: 'New cost object', center: 'New cost center', category: 'New cost category',
      driver: 'New allocation driver', rule: 'New allocation rule', reverse: 'Reverse allocation run',
      plan: 'New historical cost version', analysis: 'Calculate finalized period',
      pricing: 'Pre-sale margin check', scenario: 'New non-posting scenario',
      benefit: 'New cost-benefit analysis',
    },
    sections: {
      objects: 'Cost objects', centers: 'Cost centers', categories: 'Cost categories',
      entries: 'Cost explorer', runs: 'Allocation runs', definitions: 'Pools, drivers & rules',
      controls: 'Analytical controls', period: 'Period profitability & variance',
      history: 'Historical Standard / Estimated / Budget cost',
      pricing: 'Pricing & pre-sale margin control', decisions: 'Management decision support',
      simulator: 'Break-even simulator',
    },
    terms: {
      direct: 'Direct cost', indirect: 'Indirect cost', contribution: 'Contribution margin',
      breakEven: 'Break-even', revenue: 'Revenue', actual: 'Actual cost', variance: 'Standard variance',
      costPrice: 'Cost / price', margin: 'Margin', minimumPrice: 'Minimum price', dimension: 'Dimension',
      status: 'Status', action: 'Action', version: 'Version', effective: 'Effective', total: 'Total',
      decision: 'Decision', record: 'Record', basis: 'Basis', outcome: 'Outcome',
      codeName: 'Code / name', type: 'Type', sourceEntity: 'Source entity', currency: 'Currency',
      validity: 'Validity', center: 'Center', branch: 'Branch', department: 'Department',
      validFrom: 'Valid from', category: 'Category', traceability: 'Traceability', behavior: 'Behavior',
      function: 'Function', control: 'Control', dateSource: 'Date / source', document: 'Document',
      classification: 'Classification', sourceAmount: 'Source amount', unattributed: 'Unattributed',
      run: 'Run', poolRule: 'Pool / rule', allocated: 'Allocated', results: 'Results',
      kind: 'Kind', definition: 'Definition',
    },
    hints: {
      controls: 'Owner-module IDs and immutable versions remain authoritative; scenarios never create accounting documents.',
      simulator: 'Interactive preview only; persisted calculations remain server-authoritative.',
    },
    fields: {
      id: 'ID', type: 'Type', sourceEntityId: 'Authoritative source entity ID', code: 'Code', name: 'Name',
      currencyCode: 'Currency', parentCostObjectId: 'Parent cost object ID', startDate: 'Start date', endDate: 'End date',
      metadataJson: 'Metadata JSON text', validFrom: 'Valid from', validTo: 'Valid to', parentCostCenterId: 'Parent center ID',
      branchId: 'Branch ID', departmentId: 'Department ID', managerUserId: 'Manager user ID',
      parentCostCategoryId: 'Parent category ID', traceability: 'Traceability', behavior: 'Behavior', function: 'Function',
      controllability: 'Controllability', relevance: 'Relevance', unit: 'Unit', formula: 'Formula (custom drivers only)',
      reason: 'Reason', kind: 'Kind', versionNumber: 'Version', effectiveFrom: 'Effective from', effectiveTo: 'Effective to',
      supersedesVersionId: 'Superseded version ID', assumptionsJson: 'Assumptions JSON',
      sourceJson: 'Budget/source version JSON or null', linesJson: 'Cost lines JSON', periodId: 'Cost period ID',
      periodFrom: 'Period from', periodTo: 'Period to', sourceCutoff: 'Source cutoff',
      standardVersionId: 'Standard version ID', estimatedVersionId: 'Estimate version ID',
      budgetVersionId: 'Budget version ID', supersedesAnalysisId: 'Superseded analysis ID',
      sourcesJson: 'Authoritative source facts JSON', analysisId: 'Analysis ID', dimension: 'Dimension',
      dimensionId: 'Dimension ID', costBasis: 'Cost basis', sellingPrice: 'Selling price', strategy: 'Strategy',
      markupPercent: 'Markup %', targetMarginPercent: 'Target margin %', minimumMarginPercent: 'Minimum margin %',
      action: 'Control', saleSourceJson: 'Quotation/order source JSON', baseAnalysisId: 'Base analysis ID',
      adjustmentsJson: 'Adjustments JSON', initialInvestment: 'Initial investment',
      annualOngoingCost: 'Annual ongoing cost', annualSavings: 'Annual savings', annualRevenue: 'Annual revenue',
      residualValue: 'Residual value', discountRatePercent: 'Discount rate %', horizonYears: 'Horizon years',
      requiresApproval: 'Approval',
    },
  },
  fa: {
    page: {
      title: 'بهای تمام‌شده و سودآوری',
      description: 'هزینه قابل ردیابی، تخصیص قطعی، انحراف دوره، سودآوری، قیمت‌گذاری و تحلیل مدیریتی.',
      summary: 'خلاصه بهای تمام‌شده',
      tenantScoped: 'داده معتبر و محدود به سازمان',
    },
    metrics: {
      objects: 'موضوعات هزینه', direct: 'هزینه مستقیم تخصیص‌یافته', unattributed: 'هزینه تخصیص‌نیافته',
      pendingRuns: 'اجراهای تخصیص در انتظار اقدام', finalizedAnalyses: 'تحلیل‌های نهایی',
      historicalVersions: 'نسخه‌های تاریخی هزینه', pricingChecks: 'کنترل‌های قیمت‌گذاری',
      pendingApprovals: 'تأییدهای در انتظار اقدام',
    },
    workbench: {
      title: 'میزکار بهای تمام‌شده',
      description: 'محاسبات سمت سرور معتبرند و هر نتیجه هویت منبع خود را حفظ می‌کند.',
      objects: 'موضوعات و ابعاد', entries: 'کاوش هزینه', allocation: 'تخصیص',
      analytics: 'سودآوری و تصمیم‌ها',
    },
    actions: {
      newObject: 'موضوع هزینه جدید', newCenter: 'مرکز هزینه جدید', newCategory: 'دسته جدید',
      newDriver: 'محرک جدید', newRule: 'قاعده جدید', approve: 'تأیید', finalize: 'نهایی‌سازی',
      reverse: 'برگشت', calculate: 'محاسبه دوره', newVersion: 'نسخه هزینه جدید',
      checkPrice: 'کنترل قیمت', newScenario: 'سناریوی جدید', costBenefit: 'هزینه-فایده',
      askVarza: 'پرسش از ورزا درباره هزینه‌ها', immutable: 'تغییرناپذیر',
    },
    dialogs: {
      object: 'موضوع هزینه جدید', center: 'مرکز هزینه جدید', category: 'دسته هزینه جدید',
      driver: 'محرک تخصیص جدید', rule: 'قاعده تخصیص جدید', reverse: 'برگشت اجرای تخصیص',
      plan: 'نسخه تاریخی هزینه جدید', analysis: 'محاسبه دوره نهایی',
      pricing: 'کنترل حاشیه پیش از فروش', scenario: 'سناریوی غیرثبت‌شونده جدید',
      benefit: 'تحلیل هزینه-فایده جدید',
    },
    sections: {
      objects: 'موضوعات هزینه', centers: 'مراکز هزینه', categories: 'دسته‌های هزینه',
      entries: 'کاوش هزینه', runs: 'اجراهای تخصیص', definitions: 'مخزن‌ها، محرک‌ها و قواعد',
      controls: 'کنترل‌های تحلیلی', period: 'سودآوری و انحراف دوره',
      history: 'هزینه تاریخی استاندارد / برآوردی / بودجه',
      pricing: 'کنترل قیمت‌گذاری و حاشیه پیش از فروش', decisions: 'پشتیبانی تصمیم مدیریت',
      simulator: 'شبیه‌ساز نقطه سربه‌سر',
    },
    terms: {
      direct: 'هزینه مستقیم', indirect: 'هزینه غیرمستقیم', contribution: 'حاشیه مشارکت',
      breakEven: 'نقطه سربه‌سر', revenue: 'درآمد', actual: 'هزینه واقعی', variance: 'انحراف استاندارد',
      costPrice: 'هزینه / قیمت', margin: 'حاشیه', minimumPrice: 'حداقل قیمت', dimension: 'بُعد',
      status: 'وضعیت', action: 'عملیات', version: 'نسخه', effective: 'تاریخ اثر', total: 'جمع',
      decision: 'تصمیم', record: 'رکورد', basis: 'مبنا', outcome: 'نتیجه',
      codeName: 'کد / نام', type: 'نوع', sourceEntity: 'موجودیت منبع', currency: 'ارز',
      validity: 'اعتبار', center: 'مرکز', branch: 'شعبه', department: 'بخش',
      validFrom: 'معتبر از', category: 'دسته', traceability: 'ردیابی‌پذیری', behavior: 'رفتار',
      function: 'کارکرد', control: 'کنترل', dateSource: 'تاریخ / منبع', document: 'سند',
      classification: 'طبقه‌بندی', sourceAmount: 'مبلغ منبع', unattributed: 'تخصیص‌نیافته',
      run: 'اجرا', poolRule: 'مخزن / قاعده', allocated: 'تخصیص‌یافته', results: 'نتایج',
      kind: 'نوع', definition: 'تعریف',
    },
    hints: {
      controls: 'شناسه‌های ماژول مالک و نسخه‌های تغییرناپذیر معتبر می‌مانند؛ سناریوها سند حسابداری ایجاد نمی‌کنند.',
      simulator: 'فقط پیش‌نمایش تعاملی است؛ محاسبات ذخیره‌شده همچنان در سرور معتبرند.',
    },
    fields: {
      id: 'شناسه', type: 'نوع', sourceEntityId: 'شناسه موجودیت معتبر منبع', code: 'کد', name: 'نام',
      currencyCode: 'ارز', parentCostObjectId: 'شناسه موضوع هزینه والد', startDate: 'تاریخ شروع', endDate: 'تاریخ پایان',
      metadataJson: 'متن JSON فراداده', validFrom: 'معتبر از', validTo: 'معتبر تا', parentCostCenterId: 'شناسه مرکز والد',
      branchId: 'شناسه شعبه', departmentId: 'شناسه بخش', managerUserId: 'شناسه کاربر مدیر',
      parentCostCategoryId: 'شناسه دسته والد', traceability: 'ردیابی‌پذیری', behavior: 'رفتار', function: 'کارکرد',
      controllability: 'کنترل‌پذیری', relevance: 'ارتباط', unit: 'واحد', formula: 'فرمول (فقط محرک سفارشی)',
      reason: 'دلیل', kind: 'نوع', versionNumber: 'نسخه', effectiveFrom: 'مؤثر از', effectiveTo: 'مؤثر تا',
      supersedesVersionId: 'شناسه نسخه جایگزین‌شده', assumptionsJson: 'JSON فرضیات',
      sourceJson: 'JSON نسخه بودجه/منبع یا null', linesJson: 'JSON خطوط هزینه', periodId: 'شناسه دوره هزینه',
      periodFrom: 'دوره از', periodTo: 'دوره تا', sourceCutoff: 'برش زمانی منبع',
      standardVersionId: 'شناسه نسخه استاندارد', estimatedVersionId: 'شناسه نسخه برآورد',
      budgetVersionId: 'شناسه نسخه بودجه', supersedesAnalysisId: 'شناسه تحلیل جایگزین‌شده',
      sourcesJson: 'JSON واقعیت‌های معتبر منبع', analysisId: 'شناسه تحلیل', dimension: 'بُعد',
      dimensionId: 'شناسه بُعد', costBasis: 'مبنای هزینه', sellingPrice: 'قیمت فروش', strategy: 'راهبرد',
      markupPercent: 'درصد افزوده', targetMarginPercent: 'درصد حاشیه هدف', minimumMarginPercent: 'درصد حداقل حاشیه',
      action: 'کنترل', saleSourceJson: 'JSON منبع پیشنهاد/سفارش', baseAnalysisId: 'شناسه تحلیل پایه',
      adjustmentsJson: 'JSON تعدیل‌ها', initialInvestment: 'سرمایه‌گذاری اولیه',
      annualOngoingCost: 'هزینه جاری سالانه', annualSavings: 'صرفه‌جویی سالانه', annualRevenue: 'درآمد سالانه',
      residualValue: 'ارزش باقیمانده', discountRatePercent: 'درصد نرخ تنزیل', horizonYears: 'افق سال',
      requiresApproval: 'تأیید',
    },
  },
  ar: {
    page: {
      title: 'التكاليف والربحية',
      description: 'تكلفة قابلة للتتبع وتخصيص حتمي وانحراف دوري وربحية وتسعير وتحليل إداري.',
      summary: 'ملخص التكاليف',
      tenantScoped: 'بيانات موثوقة ضمن المؤسسة',
    },
    metrics: {
      objects: 'موضوعات التكلفة', direct: 'التكلفة المباشرة المنسوبة', unattributed: 'التكلفة غير المنسوبة',
      pendingRuns: 'عمليات التخصيص بانتظار إجراء', finalizedAnalyses: 'التحليلات النهائية',
      historicalVersions: 'إصدارات التكلفة التاريخية', pricingChecks: 'فحوص التسعير',
      pendingApprovals: 'الموافقات بانتظار الإجراء',
    },
    workbench: {
      title: 'منضدة عمل التكاليف',
      description: 'حسابات الخادم هي المرجع، وتحتفظ كل نتيجة بهوية مصدرها.',
      objects: 'الموضوعات والأبعاد', entries: 'مستكشف التكلفة', allocation: 'التخصيص',
      analytics: 'الربحية والقرارات',
    },
    actions: {
      newObject: 'موضوع تكلفة جديد', newCenter: 'مركز تكلفة جديد', newCategory: 'فئة جديدة',
      newDriver: 'محرك جديد', newRule: 'قاعدة جديدة', approve: 'موافقة', finalize: 'إنهاء',
      reverse: 'عكس', calculate: 'حساب الفترة', newVersion: 'إصدار تكلفة جديد',
      checkPrice: 'فحص السعر', newScenario: 'سيناريو جديد', costBenefit: 'التكلفة والمنفعة',
      askVarza: 'اسأل فارزا عن التكاليف', immutable: 'غير قابل للتغيير',
    },
    dialogs: {
      object: 'موضوع تكلفة جديد', center: 'مركز تكلفة جديد', category: 'فئة تكلفة جديدة',
      driver: 'محرك تخصيص جديد', rule: 'قاعدة تخصيص جديدة', reverse: 'عكس عملية التخصيص',
      plan: 'إصدار تكلفة تاريخي جديد', analysis: 'حساب الفترة النهائية',
      pricing: 'فحص هامش ما قبل البيع', scenario: 'سيناريو جديد غير مرحل',
      benefit: 'تحليل تكلفة ومنفعة جديد',
    },
    sections: {
      objects: 'موضوعات التكلفة', centers: 'مراكز التكلفة', categories: 'فئات التكلفة',
      entries: 'مستكشف التكلفة', runs: 'عمليات التخصيص', definitions: 'المجمعات والمحركات والقواعد',
      controls: 'الضوابط التحليلية', period: 'ربحية الفترة والانحراف',
      history: 'التكلفة التاريخية المعيارية / المقدرة / الموازنة',
      pricing: 'التسعير وضبط هامش ما قبل البيع', decisions: 'دعم القرار الإداري',
      simulator: 'محاكي نقطة التعادل',
    },
    terms: {
      direct: 'التكلفة المباشرة', indirect: 'التكلفة غير المباشرة', contribution: 'هامش المساهمة',
      breakEven: 'نقطة التعادل', revenue: 'الإيراد', actual: 'التكلفة الفعلية', variance: 'الانحراف المعياري',
      costPrice: 'التكلفة / السعر', margin: 'الهامش', minimumPrice: 'الحد الأدنى للسعر', dimension: 'البعد',
      status: 'الحالة', action: 'الإجراء', version: 'الإصدار', effective: 'ساري المفعول', total: 'الإجمالي',
      decision: 'القرار', record: 'السجل', basis: 'الأساس', outcome: 'النتيجة',
      codeName: 'الرمز / الاسم', type: 'النوع', sourceEntity: 'كيان المصدر', currency: 'العملة',
      validity: 'الصلاحية', center: 'المركز', branch: 'الفرع', department: 'القسم',
      validFrom: 'صالح من', category: 'الفئة', traceability: 'قابلية التتبع', behavior: 'السلوك',
      function: 'الوظيفة', control: 'التحكم', dateSource: 'التاريخ / المصدر', document: 'المستند',
      classification: 'التصنيف', sourceAmount: 'مبلغ المصدر', unattributed: 'غير منسوبة',
      run: 'العملية', poolRule: 'المجمع / القاعدة', allocated: 'مخصص', results: 'النتائج',
      kind: 'النوع', definition: 'التعريف',
    },
    hints: {
      controls: 'تبقى معرّفات الوحدات المالكة والإصدارات غير القابلة للتغيير هي المرجع؛ ولا تنشئ السيناريوهات مستندات محاسبية.',
      simulator: 'معاينة تفاعلية فقط؛ تبقى الحسابات المحفوظة مرجعية على الخادم.',
    },
    fields: {
      id: 'المعرّف', type: 'النوع', sourceEntityId: 'معرّف كيان المصدر المرجعي', code: 'الرمز', name: 'الاسم',
      currencyCode: 'العملة', parentCostObjectId: 'معرّف موضوع التكلفة الأب', startDate: 'تاريخ البدء', endDate: 'تاريخ الانتهاء',
      metadataJson: 'نص JSON للبيانات الوصفية', validFrom: 'صالح من', validTo: 'صالح حتى', parentCostCenterId: 'معرّف المركز الأب',
      branchId: 'معرّف الفرع', departmentId: 'معرّف القسم', managerUserId: 'معرّف المستخدم المدير',
      parentCostCategoryId: 'معرّف الفئة الأب', traceability: 'قابلية التتبع', behavior: 'السلوك', function: 'الوظيفة',
      controllability: 'قابلية التحكم', relevance: 'الصلة', unit: 'الوحدة', formula: 'الصيغة (للمحركات المخصصة فقط)',
      reason: 'السبب', kind: 'النوع', versionNumber: 'الإصدار', effectiveFrom: 'ساري من', effectiveTo: 'ساري حتى',
      supersedesVersionId: 'معرّف الإصدار المستبدل', assumptionsJson: 'JSON الافتراضات',
      sourceJson: 'JSON إصدار الموازنة/المصدر أو null', linesJson: 'JSON بنود التكلفة', periodId: 'معرّف فترة التكلفة',
      periodFrom: 'الفترة من', periodTo: 'الفترة إلى', sourceCutoff: 'وقت قطع المصدر',
      standardVersionId: 'معرّف الإصدار المعياري', estimatedVersionId: 'معرّف الإصدار المقدر',
      budgetVersionId: 'معرّف إصدار الموازنة', supersedesAnalysisId: 'معرّف التحليل المستبدل',
      sourcesJson: 'JSON حقائق المصدر المرجعية', analysisId: 'معرّف التحليل', dimension: 'البعد',
      dimensionId: 'معرّف البعد', costBasis: 'أساس التكلفة', sellingPrice: 'سعر البيع', strategy: 'الاستراتيجية',
      markupPercent: 'نسبة الزيادة', targetMarginPercent: 'نسبة الهامش المستهدف', minimumMarginPercent: 'نسبة الحد الأدنى للهامش',
      action: 'التحكم', saleSourceJson: 'JSON مصدر العرض/الطلب', baseAnalysisId: 'معرّف التحليل الأساسي',
      adjustmentsJson: 'JSON التعديلات', initialInvestment: 'الاستثمار الأولي',
      annualOngoingCost: 'التكلفة الجارية السنوية', annualSavings: 'التوفير السنوي', annualRevenue: 'الإيراد السنوي',
      residualValue: 'القيمة المتبقية', discountRatePercent: 'نسبة الخصم', horizonYears: 'سنوات الأفق',
      requiresApproval: 'الموافقة',
    },
  },
} as const;
